import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from 'vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import CmsNewsAttachment from '@/modules/Statistics/CMS/components/NewsAttachment.vue';
import RichTextEditor from '@/components/views/CmsIndex/RichTextEditor.vue';
import News from '@/modules/Statistics/CMS/api/models/News';
import { NewsForm } from '@/interfaces/components/news/NewsForm';
import LocalePicker from '@/components/global/LocalePicker.vue';
import LocaleRepository from '@/repositories/LocaleRepository';

interface Props {
    id?: string;
    isReadOnly?: boolean;
    isAdmin?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NewsAddNew',
  props: {
    id: { default: undefined },
    isReadOnly: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false }
  },
  emits: ["closePopup"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'closePopup'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { id: string, isReadOnly: boolean, isAdmin: boolean };





const { proxy } = getCurrentInstance()!;

const loadingOverlay = new LoadingOverlayHelper(proxy as any, {});
const isLoaded = ref(false);

const isEditMode = computed(() => {
    return !!props.id;
});

const currentLocale = computed(() => {
    if ((proxy as any).$i18n == null) {
        return 'sl';
    }
    return (proxy as any).$i18n.locale;
});

const locales = computed(() => {
    return LocaleRepository.getAll();
});

// Main object for creating reports
const formData = ref<NewsForm>({
    title: '',
    content: '',
    locale: {
        code: currentLocale.value,
        id: '',
    },
});

function checkAreFieldsAreValid() {
    if (!formData.value.title) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Please add a title!'),
            description: '',
        });
        return false;
    }

    if (!formData.value.content) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Please add a content!'),
            description: '',
        });
        return false;
    }

    return true;
}

function setLocaleIdForBE() {
    const locale = locales.value.find((localeResource) => localeResource.code === formData.value.locale.code);
    if (locale) {
        formData.value.locale.id = locale.id;
    }
}

// Format data and send to BE
async function createReport() {
    if (!props.isAdmin) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error updating news'),
            description: proxy?.$t('You are not authorized to edit news'),
        });
        return;
    }

    if (!checkAreFieldsAreValid()) {
        return;
    }

    setLocaleIdForBE();
    loadingOverlay.start();

    try {
        // API call to the backend to create a new report and attachments in that order
        if (isEditMode.value) {
            await News.updateExisting(props.id!, formData.value);
            EventBus.$emit(EventBusEvents.uploadNewsAttachment, props.id);
        } else {
            const newsData = await News.createNew(formData.value);
            EventBus.$emit(EventBusEvents.uploadNewsAttachment, newsData.data.id);
        }
    } catch (err) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error while ' + (isEditMode.value ? 'updating' : 'creating') + 'news'),
            description: '',
        });
        return;
    } finally {
        loadingOverlay.stop();
    }

    (proxy as any).$notification.success({
        message: proxy?.$t('News ' + (isEditMode.value ? 'updated' : 'created') + ' successfully'),
        description: '',
    });

    // Proceed to the reports list
    emit('closePopup');
}

onMounted(async () => {
    if (!isEditMode.value) {
        return;
    }

    loadingOverlay.start();
    isLoaded.value = false;

    let news;

    try {
        news = await News.getById(props.id!);
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error while loading news'),
            description: '',
        });
    } finally {
        loadingOverlay.stop();
        isLoaded.value = true;
    }

    if (news) {
        formData.value = {
            title: news.data.title,
            content: news.data.content,
            locale: {
                code: news.data?.locale?.code ?? currentLocale.value,
                id: news.data?.locale?.id ?? '',
            },
        };
    }
});

(function () {
    EventBus.$on(EventBusEvents.createNewsReport, createReport);
})();

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.createNewsReport, createReport);
});

return { __sfc: true,props, emit, proxy, loadingOverlay, isLoaded, isEditMode, currentLocale, locales, formData, checkAreFieldsAreValid, setLocaleIdForBE, createReport, CmsNewsAttachment, RichTextEditor, LocalePicker }
}

})